import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  Grid,
  Card,
  CardContent,
  Badge,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Drawer,
  useMediaQuery
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inbox as InboxIcon,
  Campaign as CampaignIcon,
  VideoLibrary as VideoLibraryIcon,
  AccountBalance as WalletIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  AttachMoney as MoneyIcon,
  Notifications as NotificationsIcon,
  PlayArrow as PlayIcon,
  Check as CheckIcon,
  Pending as PendingIcon
} from '@mui/icons-material';

// Import dashboard components
import Inbox from '../components/dashboard/Inbox';
import Campaigns from '../components/dashboard/Campaigns';
import Submissions from '../components/dashboard/Submissions';
import Wallet from '../components/dashboard/Wallet';
import Profile from '../components/dashboard/Profile';

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [stats, setStats] = useState({
    earnings: 0,
    spend: 0,
    views: 0,
    activeCampaigns: 0,
    submissions: 0,
    notifications: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        
        // Fetch additional user data from Firestore
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            setUserData(userDoc.data());
            
            // Fetch user stats and activity
            await fetchUserStats(currentUser.uid);
            await fetchRecentActivity(currentUser.uid);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [navigate]);

  const fetchUserStats = async (userId) => {
    try {
      // Fetch campaigns created by user
      const campaignsQuery = query(
        collection(db, "campaigns"),
        where("creatorId", "==", userId)
      );
      
      // Fetch submissions by user
      const submissionsQuery = query(
        collection(db, "submissions"),
        where("clipperId", "==", userId)
      );
      
      // Fetch user's wallet/earnings
      const walletDocRef = doc(db, "wallets", userId);
      const walletDoc = await getDoc(walletDocRef);
      
      // Mock stats for now - replace with actual data
      setStats({
        earnings: walletDoc.exists() ? walletDoc.data().balance || 0 : 0,
        spend: 1250.75,
        views: 45230,
        activeCampaigns: 3,
        submissions: 12,
        notifications: 2
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchRecentActivity = async (userId) => {
    try {
      // Mock recent activity - replace with actual queries
      setRecentActivity([
        {
          id: 1,
          type: 'submission_approved',
          message: 'Your submission for "Gaming Highlights" was approved',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
          amount: 25.00
        },
        {
          id: 2,
          type: 'campaign_created',
          message: 'New campaign "Workout Motivation" created successfully',
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000)
        },
        {
          id: 3,
          type: 'payout_received',
          message: 'Payout of $150.00 has been processed',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
          amount: 150.00
        }
      ]);
    } catch (error) {
      console.error("Error fetching activity:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'inbox', label: 'Inbox', icon: <InboxIcon />, badge: stats.notifications },
    { id: 'campaigns', label: 'Active Campaigns', icon: <CampaignIcon /> },
    { id: 'submissions', label: 'My Submissions', icon: <VideoLibraryIcon /> },
    { id: 'wallet', label: 'Wallet', icon: <WalletIcon /> },
    { id: 'profile', label: 'Profile', icon: <PersonIcon /> }
  ];

  const StatCard = ({ title, value, icon, color = 'primary', prefix = '', suffix = '' }) => (
    <Card sx={{ 
      height: '100%', 
      borderRadius: 2, 
      boxShadow: 2,
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4
      }
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              color="text.secondary" 
              gutterBottom 
              variant="overline"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                color: `${color}.main`, 
                fontWeight: 'bold',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
            </Typography>
          </Box>
          <Box sx={{ 
            color: `${color}.main`, 
            opacity: 0.7,
            '& .MuiSvgIcon-root': {
              fontSize: { xs: '2rem', sm: '2.5rem' }
            }
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const DrawerContent = () => (
    <Box sx={{ width: 250, pt: 2 }}>
      <List>
        {menuItems.map((item) => (
          <ListItem 
            button 
            key={item.id}
            onClick={() => {
              setCurrentPage(item.id);
              if (isMobile) setDrawerOpen(false);
            }}
            selected={currentPage === item.id}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 82, 82, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 82, 82, 0.2)',
                }
              }
            }}
          >
            <ListItemIcon sx={{ color: currentPage === item.id ? '#ff5252' : 'inherit' }}>
              {item.badge ? (
                <Badge badgeContent={item.badge} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText 
              primary={item.label} 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  color: currentPage === item.id ? '#ff5252' : 'inherit',
                  fontWeight: currentPage === item.id ? 'bold' : 'normal'
                } 
              }} 
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const DashboardOverview = () => (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 3, 
          fontWeight: 'bold',
          fontSize: { xs: '1.75rem', sm: '2.125rem' },
          textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        Welcome back, {user?.displayName || userData?.name || 'User'}!
      </Typography>
      
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard 
            title="Total Earnings" 
            value={stats.earnings} 
            icon={<MoneyIcon fontSize="large" />} 
            color="success"
            prefix="$"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard 
            title="Total Spend" 
            value={stats.spend} 
            icon={<TrendingUpIcon fontSize="large" />} 
            color="warning"
            prefix="$"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard 
            title="Total Views" 
            value={stats.views} 
            icon={<VisibilityIcon fontSize="large" />} 
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard 
            title="Active Campaigns" 
            value={stats.activeCampaigns} 
            icon={<CampaignIcon fontSize="large" />} 
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard 
            title="Submissions" 
            value={stats.submissions} 
            icon={<VideoLibraryIcon fontSize="large" />} 
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard 
            title="Notifications" 
            value={stats.notifications} 
            icon={<NotificationsIcon fontSize="large" />} 
            color="error"
          />
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Recent Activity
            </Typography>
            <List sx={{ py: 0 }}>
              {recentActivity.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon>
                      {activity.type === 'submission_approved' && <CheckIcon color="success" />}
                      {activity.type === 'campaign_created' && <CampaignIcon color="primary" />}
                      {activity.type === 'payout_received' && <MoneyIcon color="success" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.message}
                      secondary={activity.timestamp.toLocaleString()}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                    />
                    {activity.amount && (
                      <Chip 
                        label={`+$${activity.amount}`} 
                        color="success" 
                        size="small" 
                      />
                    )}
                  </ListItem>
                  {index < recentActivity.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button 
                variant="contained" 
                startIcon={<CampaignIcon />}
                onClick={() => setCurrentPage('campaigns')}
                fullWidth
                sx={{ 
                  bgcolor: '#ff5252', 
                  '&:hover': { bgcolor: '#ff3939' },
                  borderRadius: 2,
                  py: 1.5
                }}
              >
                Create Campaign
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<VideoLibraryIcon />}
                onClick={() => setCurrentPage('campaigns')}
                fullWidth
                sx={{ 
                  borderColor: '#ff5252', 
                  color: '#ff5252', 
                  '&:hover': { 
                    borderColor: '#ff3939', 
                    bgcolor: 'rgba(255, 82, 82, 0.1)' 
                  },
                  borderRadius: 2,
                  py: 1.5
                }}
              >
                Browse Campaigns
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<WalletIcon />}
                onClick={() => setCurrentPage('wallet')}
                fullWidth
                sx={{ 
                  borderColor: '#ff5252', 
                  color: '#ff5252', 
                  '&:hover': { 
                    borderColor: '#ff3939', 
                    bgcolor: 'rgba(255, 82, 82, 0.1)' 
                  },
                  borderRadius: 2,
                  py: 1.5
                }}
              >
                View Wallet
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardOverview />;
      case 'inbox':
        return <Inbox user={user} />;
      case 'campaigns':
        return <Campaigns user={user} />;
      case 'submissions':
        return <Submissions user={user} />;
      case 'wallet':
        return <Wallet user={user} />;
      case 'profile':
        return <Profile user={user} userData={userData} />;
      default:
        return <DashboardOverview />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ bgcolor: '#ff5252', zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 'bold'
            }}
          >
            Clipping4u Dashboard
          </Typography>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
          >
            {user?.photoURL ? (
              <Avatar src={user.photoURL} alt={user.displayName || 'User'} sx={{ width: 32, height: 32 }} />
            ) : (
              <AccountCircleIcon />
            )}
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => { setCurrentPage('profile'); handleMenuClose(); }}>Profile</MenuItem>
            <MenuItem onClick={handleMenuClose}>Settings</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={isMobile ? drawerOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          width: 250,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 250,
            boxSizing: 'border-box',
            top: '64px',
            height: 'calc(100% - 64px)',
          },
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          mt: '64px',
          ml: 0,
          minHeight: 'calc(100vh - 64px)',
          bgcolor: '#f5f5f5',
          width: '100vw',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}
      >
        {renderCurrentPage()}
      </Box>
    </Box>
  );
}